import React, { useState } from "react"
import { AdyenPaymentProvider, CheckoutOrder } from "../../../models/order"
import { useElements, useStripe } from "@stripe/react-stripe-js"
import {
  logCheckoutFailed,
  logCheckoutConfirmed,
} from "../../../context/analytics-handlers"
import { useTranslation } from "react-i18next"
import { useI18next } from "gatsby-plugin-react-i18next"
import { translationKeys } from "../../../util/translationKeys"
import { usePaymentService } from "../../../hooks/use-payment-service"
import SSESubscriberService from "../../../services/SSESubscriberService"
import { getErrorMessage } from "../../../util/error-handler"
import { AdyenCardForm } from "./AdyenCardForm"
import { useMakeAdyenPayment } from "../../../services/queries"
import { PaymentData } from "@adyen/adyen-web"
import { AdyenPaymentMethod } from "../../../models/payment-methods"
import { trackGTMPurchase } from "../../../services/gtm-datalayer-handlers"
import { useAPI } from "../../../services/api"
import { captureException } from "../../../services/exception-logger"
import { trackPaymentStage } from "../../../services/payment-telemetry"

type Props = {
  nextAction: AdyenPaymentProvider
  adyenContext: AdyenPaymentMethod
  hide: () => void
  showPaymentFailedScreen: (
    errorMessage?: string,
    providerName?: string
  ) => void
  showPaymentSucceededScreen: () => void
  paymentProcessing: boolean
  sseSubscriber: React.MutableRefObject<SSESubscriberService | undefined>
  setPaymentProcessing: React.Dispatch<React.SetStateAction<boolean>>
  startTimeout: (preCancelAction: () => void) => void
  setTimeoutId: (val: NodeJS.Timeout | undefined) => void
  timeoutId: React.MutableRefObject<NodeJS.Timeout | undefined>
  order: CheckoutOrder
}

export const AdyenCardPaymentForm = ({
  hide,
  nextAction,
  startTimeout,
  setTimeoutId,
  timeoutId,
  paymentProcessing,
  setPaymentProcessing,
  sseSubscriber,
  showPaymentFailedScreen,
  showPaymentSucceededScreen,
  order,
  adyenContext,
}: Props) => {
  const [userActionsDone, setUserActionsDone] = useState(false)
  const [isBackClickedLoading, setIsBackClickedLoading] = useState(false)
  const stripe = useStripe()
  const elements = useElements()
  const { handleCancelPayment } = usePaymentService()
  const { mutateAsync: makeAdyenPayment } = useMakeAdyenPayment()
  const { t } = useTranslation()
  const { language } = useI18next()
  const api = useAPI()

  const handlePaymentCompleted = async () => {
    trackPaymentStage("adyen", "payment-completed", {
      orderId: order.order.id,
    })
    const updatedOrder = await api.checkout.retrieveOrderById(
      order.order.id,
      language
    )
    if (updatedOrder) {
      logCheckoutConfirmed(updatedOrder.order)
      trackGTMPurchase(updatedOrder.order)
    }
    showPaymentSucceededScreen()
  }

  const onBackClicked = async () => {
    if (paymentProcessing) {
      return
    }
    setIsBackClickedLoading(true)
    sseSubscriber?.current?.close()
    try {
      await handleCancelPayment(order.order.id, showPaymentSucceededScreen)
      hide()
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, t)
      captureException(error, {
        type: "network",
        target: "internal-api",
        category: "payment",
        step: "cancel-payment",
        provider: "adyen",
        context: {
          errorSource: "AdyenCardPaymentForm - onBackClicked",
          errorMessage,
          orderId: order.order.id,
        },
      })
      showPaymentFailedScreen(errorMessage, "Adyen card")
    } finally {
      setIsBackClickedLoading(false)
    }
  }

  if (!stripe || !elements) {
    return null
  }

  const handleStartTimeout = () => {
    startTimeout(() => {
      // If userActionsDone is false, then the user is still in the process of confirming 3dsecure
      // Then we start another timeout to wait for the user to finish the process
      if (!userActionsDone) {
        clearTimeout(timeoutId.current)
        handleStartTimeout()
        // Returning true to stop cancellation
        return true
      }
      if (sseSubscriber) {
        sseSubscriber?.current?.close()
      }
    })
  }

  const onSubmit = async (state: PaymentData) => {
    setPaymentProcessing(true)
    // Starting timeout
    handleStartTimeout()
    try {
      const data = await makeAdyenPayment({
        url: nextAction.payment_url,
        data: state,
      })

      trackPaymentStage("adyen", "payment-submitted", {
        orderId: order.order.id,
        resultCode: data?.resultCode,
      })
      setUserActionsDone(true)
      return data
    } catch (error: any) {
      logCheckoutFailed(error)
      const hasCancelled = await handleCancelPayment(
        order.order.id,
        showPaymentSucceededScreen
      )
      const errorMessage = getErrorMessage(error, t)
      captureException(error, {
        type: "network",
        target: "internal-api",
        category: "payment",
        step: "provider-action",
        provider: "adyen",
        context: {
          errorSource: "AdyenCardPaymentForm - onSubmit",
          errorMessage,
          orderId: order.order.id,
          hasCancelled,
        },
      })
      if (hasCancelled) {
        showPaymentFailedScreen(errorMessage, "Adyen card")
      }
    } finally {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current)
        setTimeoutId(undefined)
      }
      setPaymentProcessing(false)
    }
  }

  return (
    <AdyenCardForm
      goBack={onBackClicked}
      title={t(translationKeys.webPayment)}
      subtitle={t(translationKeys.webPaymentSubtitle)}
      onSubmit={onSubmit}
      formSubmissionLoading={paymentProcessing}
      amount={order.order.total_remaining_price}
      countryCode={adyenContext.country_code}
      paymentDetailsUrl={nextAction.payment_details_url}
      cancelPaymentLoading={isBackClickedLoading}
      clientKey={nextAction.client_key}
      paymentMethodsUrl={adyenContext.payment_methods_url}
      onPaymentFailed={showPaymentFailedScreen}
      onPaymentCompleted={handlePaymentCompleted}
    />
  )
}
